import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import s from './Table.module.scss';
import classNames from 'classnames';
import Button from '../Button/Button';
import Icon from '../Icon';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import ExportSelect from '../ExportSelect/ExportSelect';
import * as linkify from 'linkifyjs';
import { cellColor, columnFilter, columnRanges } from './tableColorScale';

const truncateMiddle = (str, max = 50) =>
    str.length <= max ? str : `${str.slice(0, 30)}…${str.slice(-15)}`;

const renderCellContent = (value) => {
    if (typeof value !== 'string' || !value) return value;
    const tokens = linkify.tokenize(value);
    if (!tokens.some((token) => token.isLink)) return value;
    return tokens.map((token, index) =>
        token.isLink ? (
            <a
                key={index}
                href={token.toHref()}
                title={token.toString()}
                target="_blank"
                rel="noopener noreferrer">
                {truncateMiddle(token.toString())}
            </a>
        ) : (
            token.toString()
        )
    );
};

const Table = ({
    title,
    showTitleInToc,
    table,
    colorScale = 'off',
    scaleColumns = '',
}) => {
    const {
        data: rows = [],
        tableCaption,
        firstColIsHeader = false,
        firstRowIsTableHeader = false,
    } = table || {};
    const firstColAndRowAreHeaders = firstColIsHeader && firstRowIsTableHeader;

    const bodyRows = firstRowIsTableHeader ? rows.slice(1) : rows;
    const scaleRanges = useMemo(() => {
        if (colorScale === 'off') {
            return [];
        }

        const wanted = columnFilter(scaleColumns);

        return columnRanges(bodyRows, firstColIsHeader).map((range, index) =>
            !wanted || wanted.has(index) ? range : undefined
        );
    }, [bodyRows, colorScale, firstColIsHeader, scaleColumns]);

    const [isFullscreen, setIsFullscreen] = useState(false);

    const scrollRef = useRef(null);
    const [hasScroll, setHasScroll] = useState(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScroll = () => {
            setHasScroll(el.scrollWidth > el.clientWidth);
        };

        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(el);

        window.addEventListener('resize', checkScroll);

        checkScroll();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkScroll);
        };
    }, []);

    useEffect(() => {
        if (isFullscreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isFullscreen]);

    const handleDownloadXlsx = () => {
        if (!rows || rows.length === 0) return;

        const ws = XLSX.utils.aoa_to_sheet(rows);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        XLSX.writeFile(wb, `${title || 'table'}.xlsx`);
    };

    const handleDownloadImage = async () => {
        if (!scrollRef.current) return;

        const tableEl = scrollRef.current.querySelector('table');
        if (!tableEl) return;

        const exportEl = document.createElement('div');
        exportEl.style.position = 'absolute';
        exportEl.style.width = `${tableEl.offsetWidth}px`;
        exportEl.style.left = '-9999px';
        exportEl.style.top = '0';
        exportEl.style.background = 'transparent';
        exportEl.style.fontFamily = 'inherit';
        exportEl.style.borderBottom = '1px solid #e0e0e0';

        exportEl.classList.add(s.Export);

        const titleEl = document.createElement('h3');
        titleEl.textContent = title;
        titleEl.style.marginBottom = '16px';
        exportEl.appendChild(titleEl);

        const clonedTable = tableEl.cloneNode(true);
        exportEl.appendChild(clonedTable);

        document.body.appendChild(exportEl);

        const canvas = await html2canvas(exportEl, {
            scale: 5,
            useCORS: true,
            backgroundColor: '#fff',
        });

        document.body.removeChild(exportEl);

        const imgData = canvas.toDataURL('image/webp', 0.9);
        const link = document.createElement('a');
        link.href = imgData;
        link.download = `${title || 'table'}.webp`;
        link.click();
    };

    const renderCell = (cell, isHeader, key, color) => {
        const cellValue = typeof cell === 'string' ? cell : '';

        if (isHeader) {
            return (
                <th key={key} scope="row">
                    {renderCellContent(cellValue)}
                </th>
            );
        }

        return (
            <td
                key={key}
                className={classNames({ [s['Cell--Scaled']]: color })}
                data-ink={color?.ink}
                style={color ? { backgroundColor: color.background } : null}>
                {renderCellContent(cellValue)}
            </td>
        );
    };

    const renderHeader = () => (
        <thead>
            <tr>
                {rows[0]?.map((cell, index) => {
                    const cellValue = typeof cell === 'string' ? cell : '';

                    return <th key={index}>{renderCellContent(cellValue)}</th>;
                })}
            </tr>
        </thead>
    );

    const renderBody = () => (
        <tbody>
            {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                    {row.map((cell, colIndex) => {
                        const isHeader = firstColIsHeader && colIndex === 0;

                        return renderCell(
                            cell,
                            isHeader,
                            `${rowIndex}-${colIndex}`,
                            isHeader
                                ? null
                                : cellColor(
                                      cell,
                                      scaleRanges[colIndex],
                                      colorScale
                                  )
                        );
                    })}
                </tr>
            ))}
        </tbody>
    );

    const rootClasses = classNames(s.Root, {
        [s['Root--HasScroll']]: hasScroll,
        [s['Root--FirstColIsHeader']]: firstColIsHeader,
        [s['Root--FirstColAndRowAreHeaders']]: firstColAndRowAreHeaders,
    });

    return (
        <>
            <div className={rootClasses}>
                <div className={s.TitleWrapper}>
                    <h3
                        className={s.Title}
                        data-hide-title-in-toc={!showTitleInToc}>
                        {title}
                    </h3>
                    <span className={s.Description}>{tableCaption}</span>
                </div>
                <div className={s.TableWrapper}>
                    <div ref={scrollRef} className={s.ScrollContainer}>
                        <table className={s.Table}>
                            {firstRowIsTableHeader && renderHeader()}
                            {renderBody()}
                        </table>
                    </div>
                </div>
                <div className={s.ButtonsWrapper}>
                    <Button
                        modifier="Secondary"
                        size="Large"
                        title="View full table"
                        onClick={() => setIsFullscreen(true)}
                    />
                    <ExportSelect
                        onImage={handleDownloadImage}
                        onXlsx={handleDownloadXlsx}
                        openPopoverOverButton={true}
                    />
                </div>

                {isFullscreen && (
                    // A copy of the table above, so not what report comments
                    // are left on.
                    <div
                        className={s.FullscreenOverlay}
                        data-not-commentable
                        onClick={() => setIsFullscreen(false)}>
                        <div
                            className={s.FullscreenInner}
                            onClick={(e) => e.stopPropagation()}>
                            <div className={s.TitleWrapper}>
                                <h3 className={s.Title}>{title}</h3>
                                <span className={s.Description}>
                                    {tableCaption}
                                </span>
                            </div>
                            <div className={s.TableWrapper}>
                                <div className={s.ScrollContainerFull}>
                                    <table className={s.Table}>
                                        {firstRowIsTableHeader &&
                                            renderHeader()}
                                        {renderBody()}
                                    </table>
                                </div>
                            </div>
                            <div className={s.ButtonsWrapper}>
                                <Button
                                    modifier="Secondary"
                                    size="Large"
                                    title="Close"
                                    onClick={() => setIsFullscreen(false)}
                                />
                                <ExportSelect
                                    onImage={handleDownloadImage}
                                    onXlsx={handleDownloadXlsx}
                                    openPopoverOverButton={true}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

Table.propTypes = {
    title: PropTypes.string,
    table: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
        tableCaption: PropTypes.string,
        firstColIsHeader: PropTypes.bool,
        firstRowIsTableHeader: PropTypes.bool,
    }),
    colorScale: PropTypes.oneOf([
        'off',
        'blue',
        'blue_reversed',
        'red',
        'red_reversed',
        'blue_red',
        'red_blue',
        'rating',
    ]),
    scaleColumns: PropTypes.string,
};

export default Table;
